/**
 * FHIR Source Configuration
 *
 * Defines available FHIR data sources for VirtualPAT.
 * - Greenfield: Meditech sandbox/development environment
 * - HCA: HCA TriStar production environment (future)
 */

// Available FHIR sources
export const FHIR_SOURCES = {
  greenfield: {
    key: 'greenfield',
    name: 'Meditech Greenfield',
    description: 'Sandbox environment for development and testing',
    mode: 'sandbox',
    icon: 'Science', // MUI icon name
    color: '#4caf50', // Green for sandbox
  },
  hca: {
    key: 'hca',
    name: 'HCA TriStar',
    description: 'Production EHR integration (Nashville)',
    mode: 'production',
    icon: 'LocalHospital', // MUI icon name
    color: '#1976d2', // Blue for production
  },
};

// Local storage key for persisting source selection
const FHIR_SOURCE_KEY = 'fhirSource';

/**
 * Get the currently selected FHIR source
 * @returns {string} Source key ('greenfield' or 'hca')
 */
export function getFhirSource() {
  try {
    const stored = localStorage.getItem(FHIR_SOURCE_KEY);
    if (stored && FHIR_SOURCES[stored]) {
      return stored;
    }
  } catch {
    // localStorage not available
  }
  return 'greenfield'; // Default to sandbox
}

/**
 * Set the active FHIR source
 * @param {string} source - Source key
 * @returns {boolean} True if successful
 */
export function setFhirSource(source) {
  if (!FHIR_SOURCES[source]) {
    console.warn(`[FHIRSources] Unknown source: ${source}`);
    return false;
  }
  try {
    localStorage.setItem(FHIR_SOURCE_KEY, source);
    // Dispatch event for other components to react
    window.dispatchEvent(
      new CustomEvent('fhir:source-changed', { detail: { source } })
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Get configuration for a specific source
 * @param {string} source - Source key
 * @returns {Object|null} Source configuration
 */
export function getFhirSourceConfig(source) {
  return FHIR_SOURCES[source] || null;
}

/**
 * Get all available FHIR sources
 * @returns {Object} All source configurations
 */
export function getAllFhirSources() {
  return FHIR_SOURCES;
}

/**
 * Check if a source is the sandbox/development environment
 * @param {string} source - Source key
 * @returns {boolean}
 */
export function isSandboxSource(source) {
  const config = FHIR_SOURCES[source];
  return config?.mode === 'sandbox';
}

/**
 * Check if a source is production
 * @param {string} source - Source key
 * @returns {boolean}
 */
export function isProductionSource(source) {
  const config = FHIR_SOURCES[source];
  return config?.mode === 'production';
}

/**
 * Get HTTP headers for FHIR source routing
 * @param {string} source - Optional source override
 * @returns {Object} Headers object
 */
export function getFhirSourceHeaders(source) {
  const activeSource = source || getFhirSource();
  return {
    'X-FHIR-Source': activeSource,
  };
}

export default {
  FHIR_SOURCES,
  getFhirSource,
  setFhirSource,
  getFhirSourceConfig,
  getAllFhirSources,
  isSandboxSource,
  isProductionSource,
  getFhirSourceHeaders,
};
